import { useSelector } from "react-redux";
import { ArrowRightIcon } from "@app/design-system/icons";
import { cx } from "@app/shared/utils/cx";
import { RootState } from "@app/store";
import { t } from "@app/utils/translation";
import styles from "./CustomFieldsNavButton.module.css";

export type CustomFieldsNavButtonProps = {
  count: number;
  onClick: () => void;
  className?: string;
  /** Overrides the default "N custom fields" aria-label for non-custom-field
   * reuses of this control (e.g. Order Details' LastEventCard, badging a
   * total events count instead). */
  ariaLabel?: string;
};

/**
 * The "N →" badge + arrow trailing control shared by every section that
 * navigates to a details page for a count of related items — first used by
 * Order Details' Customer and Driver sections (custom fields), now its
 * LastEventCard too (total events). Per the project's "each entity owns its
 * own custom fields" rule, Pickup/Dropoff sections are expected to reuse
 * this same button rather than reinvent it.
 *
 * Callers gate on `count > 0` before rendering this at all (see
 * CustomerInfoRow/DriverCard/LastEventCard) — hiding it entirely, not just
 * disabling it, is the "nothing to show" behavior every one of these
 * sections shares.
 */
export const CustomFieldsNavButton = ({ count, onClick, className, ariaLabel }: CustomFieldsNavButtonProps) => {
  const language = useSelector((state: RootState) => state.setting.language);

  return (
    <button
      type="button"
      className={cx(styles.button, className)}
      onClick={onClick}
      aria-label={ariaLabel ?? `${t("ViewCustomFields", language)} (${count})`}
    >
      <span className={styles.badge}>{count}</span>
      <ArrowRightIcon />
    </button>
  );
};
